import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { settingsApi } from "@/lib/api";

const inputCls = "bg-[#0B131E] border-[#1E2D40] text-white placeholder:text-[#4A5A73] focus-visible:ring-[#18BFFF] focus-visible:border-[#18BFFF]";

export default function SettingsDialog({ open, onOpenChange }) {
  const [company, setCompany] = useState({ name: "", website: "", country: "", address: "", city: "", zip: "", phone: "", email: "" });
  const [tpl, setTpl] = useState({ primary_color: "#18BFFF", header_color: "#0B1F38", background_color: "#07111D", card_background: "#101B28", header_image: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    Promise.all([settingsApi.getCompany(), settingsApi.getTemplate()])
      .then(([c, t]) => { setCompany(c); setTpl(t); })
      .catch(() => toast.error("Failed to load settings"));
  }, [open]);

  const saveCompany = async () => {
    setSaving(true);
    try {
      await settingsApi.setCompany(company);
      toast.success("Company settings saved");
    } catch {
      toast.error("Save failed");
    } finally { setSaving(false); }
  };

  const saveTpl = async () => {
    setSaving(true);
    try {
      await settingsApi.setTemplate(tpl);
      toast.success("Template saved");
    } catch {
      toast.error("Save failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#101B28] border-[#1E2D40] text-white max-w-2xl" data-testid="settings-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Settings</DialogTitle>
        </DialogHeader>
        <Tabs defaultValue="company">
          <TabsList className="bg-[#0B131E] border border-[#1E2D40]">
            <TabsTrigger value="company" data-testid="settings-tab-company">Company</TabsTrigger>
            <TabsTrigger value="template" data-testid="settings-tab-template">Template</TabsTrigger>
          </TabsList>

          <TabsContent value="company" className="space-y-4 pt-4">
            <Grid2>
              <F label="Company name">
                <Input data-testid="settings-company-name" className={inputCls} value={company.name} onChange={(e) => setCompany({ ...company, name: e.target.value })} />
              </F>
              <F label="Website">
                <Input data-testid="settings-company-website" className={inputCls} value={company.website} onChange={(e) => setCompany({ ...company, website: e.target.value })} />
              </F>
            </Grid2>
            <Grid2>
              <F label="Country">
                <Input data-testid="settings-company-country" className={inputCls} value={company.country} onChange={(e) => setCompany({ ...company, country: e.target.value })} />
              </F>
              <F label="City">
                <Input className={inputCls} value={company.city} onChange={(e) => setCompany({ ...company, city: e.target.value })} />
              </F>
            </Grid2>
            <F label="Address">
              <Input className={inputCls} value={company.address} onChange={(e) => setCompany({ ...company, address: e.target.value })} />
            </F>
            <Grid2>
              <F label="Phone">
                <Input className={inputCls} value={company.phone} onChange={(e) => setCompany({ ...company, phone: e.target.value })} />
              </F>
              <F label="Email">
                <Input className={inputCls} value={company.email} onChange={(e) => setCompany({ ...company, email: e.target.value })} />
              </F>
            </Grid2>
            <DialogFooter>
              <Button onClick={saveCompany} disabled={saving} data-testid="settings-company-save"
                      className="bg-[#18BFFF] text-[#07111D] hover:bg-[#4DD0FF]">Save company</Button>
            </DialogFooter>
          </TabsContent>

          <TabsContent value="template" className="space-y-4 pt-4">
            <Grid2>
              <F label="Primary color">
                <ColorInput value={tpl.primary_color} onChange={(v) => setTpl({ ...tpl, primary_color: v })} testid="settings-primary_color" />
              </F>
              <F label="Header color">
                <ColorInput value={tpl.header_color} onChange={(v) => setTpl({ ...tpl, header_color: v })} testid="settings-header_color" />
              </F>
            </Grid2>
            <Grid2>
              <F label="Background color">
                <ColorInput value={tpl.background_color} onChange={(v) => setTpl({ ...tpl, background_color: v })} />
              </F>
              <F label="Card background">
                <ColorInput value={tpl.card_background} onChange={(v) => setTpl({ ...tpl, card_background: v })} />
              </F>
            </Grid2>
            <F label="Default header image URL">
              <Input className={inputCls} value={tpl.header_image} onChange={(e) => setTpl({ ...tpl, header_image: e.target.value })} />
            </F>
            <DialogFooter>
              <Button onClick={saveTpl} disabled={saving} data-testid="settings-template-save"
                      className="bg-[#18BFFF] text-[#07111D] hover:bg-[#4DD0FF]">Save template</Button>
            </DialogFooter>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

function F({ label, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-[0.15em] text-[#94A3B8]">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function Grid2({ children }) {
  return <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{children}</div>;
}

function ColorInput({ value, onChange, testid }) {
  return (
    <div className="flex gap-2">
      <Input className={inputCls} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
      <div className="w-10 h-10 rounded-md border border-[#1E2D40]" style={{ background: value }} />
    </div>
  );
}
